import React, { useCallback } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { POS_COLORS } from './posColors';

/**
 * ActionBar – mirrors POS client ActionButtons. Colors from posColors.js (POS theme).
 */
const ActionBar = ({
  selectedProduct,
  count,
  onSetCount,
  onDone,
  onCancel,
  onBack,
  onSaveBack,
  isValid,
  missingGroups,
  isInNestedContext,
  siblingData,
  onSwitchSiblingOption,
}) => {
  if (!selectedProduct) return null;

  // Common button styles
  const btnBase = {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 15,
    borderRadius: '8px',
    px: 2.5,
    py: 0.8,
    minWidth: 80,
  };

  // Handler for clicking a sibling chip
  const handleSiblingClick = useCallback(
    (optionId) => {
      if (
        !siblingData ||
        optionId === siblingData.currentOptionId ||
        !onSwitchSiblingOption
      ) {
        return;
      }
      onSwitchSiblingOption(optionId, siblingData.parentModifierGroupId);
    },
    [siblingData, onSwitchSiblingOption]
  );

  // --- Nested context: Back / Save / Done + SiblingOptionsRow ---
  if (isInNestedContext) {
    return (
      <Box
        sx={{
          bgcolor: POS_COLORS.ACTION_BAR_BG,
          borderTop: `1px solid ${POS_COLORS.BORDER_LIGHT}`,
          borderBottom: `1px solid ${POS_COLORS.BORDER_LIGHT}`,
        }}
      >
        {/* Row 1: Back + Save/Done buttons */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{
              ...btnBase,
              color: POS_COLORS.PRIMARY,
              border: `1px solid ${POS_COLORS.PRIMARY}`,
              bgcolor: 'transparent',
              '&:hover': { bgcolor: POS_COLORS.GRAY_BACKGROUND },
            }}
          >
            Back
          </Button>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              onClick={onSaveBack}
              sx={{
                ...btnBase,
                bgcolor: POS_COLORS.PRIMARY,
                color: POS_COLORS.WHITE,
                '&:hover': { bgcolor: POS_COLORS.PRIMARY_HOVER },
              }}
            >
              Save
            </Button>
            <Button
              variant="contained"
              onClick={onDone}
              disabled={!isValid}
              sx={{
                ...btnBase,
                bgcolor: POS_COLORS.PRIMARY,
                color: POS_COLORS.WHITE,
                '&:hover': { bgcolor: POS_COLORS.PRIMARY_HOVER },
                '&:disabled': {
                  bgcolor: POS_COLORS.GRAY_SLIDE,
                  color: POS_COLORS.WHITE,
                },
              }}
            >
              Done
            </Button>
          </Box>
        </Box>

        {/* Row 2: Sibling options row (only when multiplierApplyingToNestedPrice) */}
        {siblingData && siblingData.siblingOptions.length > 1 && (
          <Box
            sx={{
              display: 'flex',
              overflowX: 'auto',
              gap: '10px',
              px: 2,
              pb: 1,
              '&::-webkit-scrollbar': { height: 4 },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: POS_COLORS.GRAY_SLIDE,
                borderRadius: 2,
              },
            }}
          >
            {siblingData.siblingOptions.map((option) => {
              const isSelected = option._id === siblingData.currentOptionId;
              const isDisabled = option.isUnavailable;

              return (
                <Box
                  key={option._id}
                  onClick={() => !isDisabled && handleSiblingClick(option._id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 2.5,
                    py: 1.5,
                    borderRadius: '8px',
                    border: isSelected
                      ? `1px solid ${POS_COLORS.PRIMARY}`
                      : `1px solid ${POS_COLORS.DIVIDER}`,
                    bgcolor: isSelected ? POS_COLORS.PRIMARY : 'transparent',
                    minWidth: 100,
                    minHeight: 44,
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.5 : 1,
                    flexShrink: 0,
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      bgcolor: isDisabled
                        ? undefined
                        : isSelected
                        ? POS_COLORS.PRIMARY_HOVER
                        : POS_COLORS.SURFACE,
                    },
                  }}
                >
                  <Typography
                    noWrap
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: isSelected
                        ? POS_COLORS.WHITE
                        : isDisabled
                        ? POS_COLORS.GREY_SECONDARY
                        : POS_COLORS.BLACK,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    {option.name}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );
  }

  // --- Product level: Qty / Cancel / Done ---
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1,
        bgcolor: POS_COLORS.ACTION_BAR_BG,
        borderTop: `1px solid ${POS_COLORS.BORDER_LIGHT}`,
        borderBottom: `1px solid ${POS_COLORS.BORDER_LIGHT}`,
      }}
    >
      {/* Quantity controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          sx={{ fontWeight: 600, fontSize: 15, color: POS_COLORS.BLACK }}
        >
          Qty:
        </Typography>
        <IconButton
          size="small"
          onClick={() => onSetCount(Math.max(1, count - 1))}
          disabled={count <= 1}
          sx={{
            border: `1px solid ${POS_COLORS.PRIMARY}`,
            borderRadius: '50%',
            p: 0.4,
            '&:disabled': { borderColor: POS_COLORS.GRAY_SLIDE },
          }}
        >
          <RemoveIcon sx={{ fontSize: 18, color: POS_COLORS.BLACK }} />
        </IconButton>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 16,
            minWidth: 28,
            textAlign: 'center',
            color: POS_COLORS.BLACK,
          }}
        >
          {count}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onSetCount(count + 1)}
          sx={{
            border: `1px solid ${POS_COLORS.PRIMARY}`,
            borderRadius: '50%',
            p: 0.4,
          }}
        >
          <AddIcon sx={{ fontSize: 18, color: POS_COLORS.BLACK }} />
        </IconButton>
      </Box>

      {/* Validation warning */}
      {!isValid && missingGroups.length > 0 && (
        <Typography
          sx={{
            color: POS_COLORS.ERROR,
            fontWeight: 500,
            fontSize: 14,
            mx: 1,
            flex: 1,
            textAlign: 'center',
          }}
        >
          Required: {missingGroups.join(', ')}
        </Typography>
      )}

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          onClick={onCancel}
          sx={{
            ...btnBase,
            color: POS_COLORS.PRIMARY,
            border: `1px solid ${POS_COLORS.PRIMARY}`,
            bgcolor: 'transparent',
            '&:hover': { bgcolor: POS_COLORS.GRAY_BACKGROUND },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onDone}
          disabled={!isValid}
          sx={{
            ...btnBase,
            bgcolor: POS_COLORS.PRIMARY,
            color: POS_COLORS.WHITE,
            '&:hover': { bgcolor: POS_COLORS.PRIMARY_HOVER },
            '&:disabled': {
              bgcolor: POS_COLORS.GRAY_SLIDE,
              color: POS_COLORS.WHITE,
            },
          }}
        >
          Done
        </Button>
      </Box>
    </Box>
  );
};

export default ActionBar;
